"use client"

import React, { useState, useEffect } from "react"
import { ImagePicker } from "@/app/components/ImagePicker"
import { Note, Blog, ImageWithPlaceholder } from "@types"
import { usePostEdit } from "@/app/contexts/PostEditContext"
import { updatePost } from "@/lib/api/notes"
import { getCollectibleImageUrl } from "@/util/crypto"
import { User } from "@/types/users"
import { ImagePlus, Trash2 } from "lucide-react"
import { collectibleImageChanged } from "../../../../tracking"

export const CollectibleCard = ({
  post,
  blog,
  user,
}: {
  post: Note
  blog: Blog
  user: User
}) => {
  const { postId, setIsSaving } = usePostEdit()
  const [defaultImg, setDefaultImg] = useState<string>("")
  const [customImg, setCustomImg] = useState<string | undefined>(
    post.collectibleImgUrl
  )

  useEffect(() => {
    const imageUrl = getCollectibleImageUrl({
      post: {
        ...post,
        collectibleImgUrl: customImg,
      },
      text: post.title || "",
      blog,
      blogOwnerUser: user,
      collectibleType: "POST",
      size: 1024,
    })
    setDefaultImg(imageUrl)
  }, [post, blog, user, customImg])

  const handleUpdatePost = async (updateData: Partial<Note>) => {
    await updatePost({
      postId,
      post: updateData,
      isSavingCallback: setIsSaving,
    })
  }

  const handleImageSave = async (img: ImageWithPlaceholder) => {
    collectibleImageChanged(postId)
    setCustomImg(img.img.src)
    await handleUpdatePost({ collectibleImgUrl: img.img.src })
  }

  const handleImageDelete = async () => {
    setCustomImg("")
    await handleUpdatePost({ collectibleImgUrl: "" })
  }

  const displayedImage = customImg || defaultImg

  const customActionButtons = (
    <>
      {customImg ? (
        <button
          className="bg-white rounded-md p-1.5 shadow-md hover:bg-gray-100 transition-colors"
          onClick={handleImageDelete}
        >
          <Trash2 className="w-4 h-4 text-gray-600" />
        </button>
      ) : (
        <button
          className="bg-white rounded-md p-1.5 shadow-md hover:bg-gray-100 transition-colors"
          onClick={() => {
            // This will trigger the file input click
            const fileInput = document.querySelector(
              'input[type="file"]'
            ) as HTMLInputElement
            if (fileInput) fileInput.click()
          }}
        >
          <ImagePlus className="w-4 h-4 text-gray-600" />
        </button>
      )}
    </>
  )

  return (
    <div className="relative">
      <ImagePicker
        imgSrc={displayedImage}
        handleSave={handleImageSave}
        handleDelete={handleImageDelete}
        actionText={
          customImg
            ? "Change collectible image"
            : "Add custom collectible image"
        }
        wrapperClassName="h-full rounded-lg max-w-[512px] border-0"
        imageClassName="w-full h-full object-cover rounded-lg border"
        showDefaultDeleteButton={false}
        customActionButtons={customActionButtons}
        aspect={1}
      />
    </div>
  )
}
